import React from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Modal,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderOffRoundedIcon from "@mui/icons-material/FolderOffRounded";

const useStyles = makeStyles((theme) => ({
  boxContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    // backgroundColor: "#000000",
    // borderRadius: 14,
    // padding: theme.spacing(4),
    // width: "86%",
    overflow: "hidden",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginBottom: theme.spacing(1),
  },
  boxHeading: {
    color: "#ffffff",
    fontSize: "0.9rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
    fontFamily: "Raleway, sans-serif",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.5rem",
    },
  },
  sectionTitle: {
    color: "#ffffff",
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.5),
    fontFamily: "Raleway, sans-serif",
  },
  listContainer: {
    width: "100%",
    maxHeight: "280px", // Adjust as needed
    overflowY: "auto",
    paddingRight: theme.spacing(1), // Add some padding
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#303030", // Make track transparent
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#FFC629", // Make thumb transparent by default
      borderRadius: "4px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      backgroundColor: "#FFC629", // Show thumb on hover
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#FACC15",
    },
  },
  listItem: {
    justifyContent: "space-between",
    backgroundColor: "#303030",
    marginBottom: "0.5em",
    borderRadius: 6,
    padding: theme.spacing(0.5, 2),
  },
  text: {
    color: "#ffffff",
    fontFamily: "Poppins, sans-serif",
    fontSize: "0.8rem",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginRight: theme.spacing(2),
  },
  modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: "#202020",
    borderRadius: "9px",
    padding: theme.spacing(5),
  },
  yellowButton: {
    backgroundColor: "#FFC629",
    color: "#000000",
    borderRadius: "8px",
    padding: theme.spacing(1, 3),
    fontWeight: "bold",
    textTransform: "none",
    fontFamily: "Raleway, sans-serif",
    "&:hover": {
      backgroundColor: "#FFD700",
    },
  },
}));

const DocumentsPanel = ({
  files,
  handleClickOpen,
  open,
  handleClose,
  fileToDelete,
  handleConfirmDelete,
  loading,
  onAddClick,
  onDownload,
  downloadingFileIds,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <Box className={classes.boxContainer}>
      <Box className={classes.headerContainer}>
      <Typography className={classes.sectionTitle}>Documents</Typography>
        <Button
          variant="contained"
          className={classes.yellowButton}
          onClick={onAddClick}
        >
          + Add
        </Button>
      </Box>
      {Array.isArray(files) && files.length > 0 ? (
        <Box className={classes.listContainer}>
          <List
            style={{
              // padding: 1,
              // backgroundColor: "black",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "start",
              width: "-webkit-fill-available",
            }}
          >
            {files.map((file) => {
              const isDownloading = !!downloadingFileIds?.has?.(file.FileID);
              return (
                <ListItem key={file.FileID} className={classes.listItem}>
                  <ListItemText
                    primary={file.FileName}
                    primaryTypographyProps={{
                      style: {
                        fontFamily: "Poppins, sans-serif",
                        color: "#ffffff",
                        fontSize: "0.95rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginRight: theme.spacing(2),
                      },
                    }}
                    title={file.FileName}
                  />
                  <Box style={{ display: "flex", gap: 8 }}>
                    {onDownload && (
                      <IconButton
                        aria-label="download"
                        disabled={isDownloading}
                        style={{
                          backgroundColor: "#FFC629",
                          borderRadius: 4,
                          width: 20,
                          height: 20,
                          opacity: isDownloading ? 0.6 : 1,
                        }}
                        onClick={() => onDownload(file)}
                      >
                        {isDownloading ? (
                          <CircularProgress size={14} style={{ color: "black" }} />
                        ) : (
                          <FileDownloadOutlinedIcon
                            sx={{ color: "black", width: 20, height: 20 }}
                          />
                        )}
                      </IconButton>
                    )}
                    <IconButton
                      aria-label="delete"
                      style={{
                        backgroundColor: "#FFC629",
                        borderRadius: 4,
                        width: 20,
                        height: 20,
                      }}
                      onClick={() => handleClickOpen(file)}
                    >
                      <DeleteOutlineOutlinedIcon
                        sx={{ color: "black", width: 20, height: 20 }}
                      />
                    </IconButton>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ) : (
        <Box
          style={{
            padding: 12,
            backgroundColor: "#252525",
            display: "flex",
            borderRadius: 12,
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "-webkit-fill-available",
            color: "white",
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(4),
          }}
        >
          <Typography
            style={{
              marginBottom: 16,
              fontFamily: "Raleway, sans-serif",
              color: "#aaaaaa",
            }}
          >
            This collection is empty
          </Typography>
          <FolderOffRoundedIcon style={{ fontSize: 30, color: "#FFC629" }} />
        </Box>
      )}
      <Modal open={open} onClose={handleClose}>
        <Box className={classes.modal}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            style={{
              textAlign: "center",
              fontFamily: "Raleway",
              fontWeight: "bolder",
              color: "white",
              fontSize: "1.5rem",
              wordBreak: "break-word",
            }}
          >
            Are you sure you want to delete this file?
          </Typography>
          <Box
            style={{
              display: "flex",
              justifyContent: "space-evenly",
              marginTop: theme.spacing(3),
            }}
          >
            <Button
              disabled={loading}
              onClick={handleConfirmDelete}
              style={{
                backgroundColor: "#FFC629",
                color: "#000000",
                borderRadius: "10px",
                padding: theme.spacing(1, 3),
                fontSize: "1rem",
                fontWeight: "bold",
                textTransform: "none",
                fontFamily: "Raleway, sans-serif",
                "&:hover": {
                  backgroundColor: "#FFC629",
                  color: "#000000",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "0.9rem",
                },
              }}
            >
              {loading ? "Deleting . . ." : "Delete"}
            </Button>
            <Button
              onClick={handleClose}
              style={{ color: "white" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DocumentsPanel;
